import React, { useState, createContext, Dispatch, SetStateAction, useRef } from 'react';
import { AccountDetails, DefaultModalityStatus, IsInvalid, ModalityStatus } from '../../constants/options';
import { ClinicInfo } from './clinic-details/single-clinic-info';
import { DeepCopy } from '../../utilities/general';

interface SectionApprovalCount {
  pending: number,
  rejected: number,
  approved: number,
}

const sampleSectionApprovalCount: SectionApprovalCount = { pending: 0, rejected: 0, approved: 0 };

interface MembershipTabsApprovalStatus {
  servicesCount: SectionApprovalCount,
  addressCount: SectionApprovalCount,
  businessPracticeCount: SectionApprovalCount,
  clinicCount: SectionApprovalCount,
  complianceDocCount: SectionApprovalCount,
  otherMembershipCount: SectionApprovalCount,
  qualificationCount: SectionApprovalCount,
  paymentCount: SectionApprovalCount,
  cpdCount: SectionApprovalCount,
  declarationCount: SectionApprovalCount,
  cmbaCount: SectionApprovalCount,
}

interface MemberContextType {
  tabApprovalStatus: MembershipTabsApprovalStatus
  setTabApprovalStatus: Dispatch<SetStateAction<MembershipTabsApprovalStatus>> | any
  memberServices: ModalityStatus
  setMemberServices: Dispatch<SetStateAction<ModalityStatus>> | any
  memberClinicLimitsReached: ModalityStatus
  setMemberClinicLimitsReached: Dispatch<SetStateAction<ModalityStatus>> | any
  memberInfo: AccountDetails,
  setMemberInfo: Dispatch<SetStateAction<AccountDetails>> | any
  memberClinicList: ClinicInfo[],
  setMemberClinicList: Dispatch<SetStateAction<ClinicInfo[]>> | any,
  refreshMemberClinicFn: React.MutableRefObject<(() => void) | null>
}

const MemberContext = createContext<MemberContextType>({
  tabApprovalStatus: {
    servicesCount: sampleSectionApprovalCount,
    addressCount: sampleSectionApprovalCount,
    businessPracticeCount: sampleSectionApprovalCount,
    clinicCount: sampleSectionApprovalCount,
    complianceDocCount: sampleSectionApprovalCount,
    otherMembershipCount: sampleSectionApprovalCount,
    qualificationCount: sampleSectionApprovalCount,
    paymentCount: sampleSectionApprovalCount,
    cpdCount: sampleSectionApprovalCount,
    declarationCount: sampleSectionApprovalCount,
    cmbaCount: sampleSectionApprovalCount,
  },
  setTabApprovalStatus: (a: MembershipTabsApprovalStatus) => { },
  memberServices: DeepCopy(DefaultModalityStatus),
  setMemberServices: (a: ModalityStatus) => { },
  memberInfo: {} as AccountDetails,
  setMemberInfo: (a: AccountDetails) => { },
  memberClinicLimitsReached: DeepCopy(DefaultModalityStatus),
  setMemberClinicLimitsReached: (a: ModalityStatus) => { },
  memberClinicList: [] as ClinicInfo[],
  setMemberClinicList: (a: ClinicInfo[]) => { },
  refreshMemberClinicFn: null as unknown as React.MutableRefObject<(() => void) | null>,
});

const MemberContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [tabApprovalStatus, setTabApprovalStatus] = useState<MembershipTabsApprovalStatus>({
    servicesCount: sampleSectionApprovalCount,
    addressCount: sampleSectionApprovalCount,
    businessPracticeCount: sampleSectionApprovalCount,
    clinicCount: sampleSectionApprovalCount,
    complianceDocCount: sampleSectionApprovalCount,
    otherMembershipCount: sampleSectionApprovalCount,
    qualificationCount: sampleSectionApprovalCount,
    paymentCount: sampleSectionApprovalCount,
    cpdCount: sampleSectionApprovalCount,
    declarationCount: sampleSectionApprovalCount,
    cmbaCount: sampleSectionApprovalCount,
  });
  const [memberServices, setMemberServices] = useState(DeepCopy(DefaultModalityStatus));
  const [memberInfo, setMemberInfo] = useState<AccountDetails>({} as AccountDetails);
  const [memberClinicLimitsReached, setMemberClinicLimitsReached] = useState(DeepCopy(DefaultModalityStatus));
  const [memberClinicList, setMemberClinicList] = useState<ClinicInfo[]>([]);

  const refreshMemberClinicFn = useRef<(() => void) | null>(null);

  return (
    <MemberContext.Provider value={{
      tabApprovalStatus, setTabApprovalStatus,
      memberServices, setMemberServices,
      memberInfo, setMemberInfo,
      memberClinicLimitsReached, setMemberClinicLimitsReached,
      memberClinicList, setMemberClinicList,
      refreshMemberClinicFn,
    }}>
      {children}
    </MemberContext.Provider>
  );
};

export { MemberContext, MemberContextProvider };
